"use client";

import { Popover as PopoverPrimitive } from "@base-ui/react/popover";
import * as React from "react";

import { stopAtOverlayBoundary } from "../lib/portal-events";
import { cn } from "../lib/utils";

function Popover({ ...props }: PopoverPrimitive.Root.Props) {
  return <PopoverPrimitive.Root {...props} />;
}

function PopoverTrigger({ ...props }: PopoverPrimitive.Trigger.Props) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

function PopoverContent({
  className,
  side = "bottom",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  anchor,
  collisionAvoidance,
  instantClose = false,
  hideWhenDetached = false,
  onClick,
  onContextMenu,
  ...props
}: PopoverPrimitive.Popup.Props &
  Pick<
    PopoverPrimitive.Positioner.Props,
    | "side"
    | "sideOffset"
    | "align"
    | "alignOffset"
    | "anchor"
    | "collisionAvoidance"
  > & {
    /**
     * Skip the exit animation so the content unmounts in the same React
     * commit that closes it. Closing content stays mounted until its exit
     * transition finishes, so a popover that triggers navigation (or whose
     * anchor can be hidden while it closes) briefly lingers unanchored on
     * screen without this.
     */
    instantClose?: boolean;
    /**
     * Hide the popover while its anchor is not visible (Radix
     * hideWhenDetached parity). For open popovers whose trigger lives in a
     * CSS-hidden panel — e.g. a kept-mounted subview the user switched away
     * from — this keeps the still-open popup from floating over the new
     * view. Styled via Base UI's data-anchor-hidden positioner attribute.
     */
    hideWhenDetached?: boolean;
  }) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        collisionAvoidance={collisionAvoidance}
        className={cn(
          "isolate z-50",
          hideWhenDetached && "data-anchor-hidden:invisible",
        )}
      >
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          // Clicks inside the popover act on the popover only, never on the
          // clickable surface it floats above.
          onClick={stopAtOverlayBoundary(onClick)}
          onContextMenu={stopAtOverlayBoundary(onContextMenu)}
          className={cn(
            "bg-popover text-popover-foreground z-50 w-72 origin-(--transform-origin) rounded-md border p-4 shadow-md outline-hidden transition-[opacity,scale] duration-150 data-starting-style:scale-95 data-starting-style:opacity-0",
            !instantClose &&
              "data-ending-style:scale-95 data-ending-style:opacity-0",
            className,
          )}
          {...props}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
}

/**
 * Radix anchor passthrough. Base UI has no Anchor part (use the Positioner's
 * `anchor` prop instead); this renders its children unchanged so any legacy
 * markup keeps rendering, without influencing positioning.
 */
function PopoverAnchor({ children }: { children?: React.ReactNode }) {
  return <>{children}</>;
}

function PopoverClose({ ...props }: PopoverPrimitive.Close.Props) {
  return <PopoverPrimitive.Close data-slot="popover-close" {...props} />;
}

export { Popover, PopoverAnchor, PopoverClose, PopoverContent, PopoverTrigger };
